<template>
    <div ref="assetDashboard">
        <unlock-changeover-banner
            :show="showUnlockChangeoverDialog"
            :assetId="assetId"
        />
        <!-- Connection status banner -->
        <banner
            v-if="connectionStatus === 'offline'"
            type="warning"
            class="q-mb-md q-mr-sm"
            :message="
                $t(
                    'Gateway disconnected - A data acquisition device sending telemetry for this line is disconnected, and live data may be unavailable. Please contact on-site support.'
                )
            "
        />
        <!-- if no sensor -->
        <banner
            v-if="!initializingSensors && sensors && sensors.length === 0"
            type="warning"
            class="q-mb-md q-mr-sm"
            :message="$t('No sensors are connected to this asset.')"
        />

        <!-- End of Connection status banner -->

        <!-- Status row -->

        <div class="row justify-center">
            <!-- Asset status header label -->
            <div class="status column">
                <!-- Quality Event Detected -->
                <div
                    v-if="hasOngoingQualityEvent"
                    class="q-mb-sm row items-center justify-center"
                >
                    <q-icon
                        name="fa-solid fa-triangle-exclamation"
                        color="warning"
                        size="sm"
                        class="q-mr-sm"
                    />
                    <span v-if="!defect" class="text-subtitle1 text-capitalize">
                        {{ $t('Low Quality') }}
                    </span>
                    <span v-else class="text-subtitle1 text-capitalize">
                        {{
                            $t('Low Quality: {defectName}', {
                                defectName: defect['name']
                            })
                        }}
                    </span>
                </div>
                <div class="status__label text-uppercase">
                    <div v-if="isLoading">
                        {{ $t('Loading') }}
                    </div>
                    <div v-else class="ellipsis">
                        <q-icon
                            v-if="initializingSensors"
                            :color="ASSET_STATUS_TO_STYLE['unknown']['color']"
                            :name="ASSET_STATUS_TO_STYLE['unknown']['icon']"
                            class="status__icon q-mr-sm"
                            size="sm"
                        />
                        <q-icon
                            v-else
                            :color="ASSET_STATUS_TO_STYLE[status]['color']"
                            :name="ASSET_STATUS_TO_STYLE[status]['icon']"
                            class="status__icon q-mr-sm"
                            size="sm"
                        />
                        <span v-if="initializingSensors">{{
                            $t(ASSET_STATUS_TO_STYLE['unknown']['label'])
                        }}</span>
                        <span
                            id="asset-status"
                            v-else-if="status === 'changeover' && cause"
                            class="q-my-lg"
                        >
                            {{ cause.name }}
                            <div
                                v-if="timeDiff"
                                class="row justify-center q-py-sm q-gutter-lg"
                            >
                                <div
                                    v-if="timeDiff"
                                    class="countdown-timer text-center text-bold"
                                    :style="getCountdownStyle"
                                >
                                    {{ countdownFormat(timeDiff) }}
                                </div>
                                <q-btn
                                    id="new-changeover-button"
                                    @click="showNewChangeoverDialog = true"
                                    :disable="$userCannot('create', 'phases')"
                                    icon="fas fa-sync-alt"
                                    :label="
                                        $q.screen.lt.lg
                                            ? ''
                                            : $t('Start Another Switch')
                                    "
                                    outline
                                    color="dark-yellow"
                                    class="q-mr-sm bg-white"
                                />
                            </div>
                            <div v-else>
                                <q-spinner
                                    color="primary"
                                    size="1em"
                                    :thickness="10"
                                />
                            </div>
                        </span>
                        <span v-else id="asset-status"
                            >{{ $t(ASSET_STATUS_TO_STYLE[status]['label']) }}
                            <div
                                v-if="showPhaseCause"
                                class="text-subtitle1 text-capitalize"
                            >
                                <b>
                                    {{
                                        $t('Cause: {causeName}', {
                                            causeName: cause.name
                                        })
                                    }}
                                </b>
                            </div>
                            <div
                                class="row justify-center items-center"
                                v-if="status === 'in_downtime'"
                            >
                                <div
                                    v-if="!showPhaseCause && !loadingCause"
                                    class="q-mb-xs"
                                >
                                    <missing-cause-label
                                        v-model="causeSelectProps.selection"
                                        :phaseId="phaseId"
                                        :phaseUUID="phase.phaseUUID"
                                        :isOutlined="true"
                                        @clearSelection="
                                            causeSelectProps.selection = null
                                        "
                                        :baseURL="causeSelectProps.url"
                                        :queryParams="
                                            causeSelectProps.queryParams
                                        "
                                        :label="causeSelectProps.label"
                                        :dialogTitle="
                                            causeSelectProps.dialogTitle
                                        "
                                        :searchLabel="
                                            causeSelectProps.searchLabel
                                        "
                                        row-key="id"
                                    >
                                        <template v-slot:item="data">
                                            <cause-card
                                                @select="
                                                    selectCause(
                                                        data.props.row,
                                                        causeSelectProps
                                                    )
                                                "
                                                :name="data.props.row.name"
                                                :lossType="
                                                    data.props.row.type_of_loss
                                                "
                                                :categoryName="
                                                    data.props.row.category_name
                                                "
                                                :planned="
                                                    data.props.row.planned
                                                "
                                                :description="
                                                    data.props.row.description
                                                "
                                                :targetDowntime="
                                                    data.props.row
                                                        .target_down_time
                                                "
                                                :isSelected="
                                                    causeSelectProps.selection &&
                                                    causeSelectProps.selection
                                                        .value ===
                                                        data.props.row.id
                                                "
                                                :isSelectable="true"
                                                :equipmentFailure="
                                                    data.props.row
                                                        .equipment_failure
                                                "
                                            />
                                        </template>
                                    </missing-cause-label>
                                </div>
                                <q-btn
                                    @click="showSplitDowntimeDialog = true"
                                    :disable="$userCannot('create', 'phases')"
                                    icon="call_split"
                                    :label="$t('Split Downtime')"
                                    outline
                                    color="dark-yellow"
                                    class="q-my-sm q-ml-sm bg-white"
                                >
                                    <q-tooltip>
                                        {{ $t('Split the current downtime') }}
                                    </q-tooltip>
                                </q-btn>
                            </div>
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Buttons row -->
        <div v-if="!isEdit" class="row q-mb-sm justify-between">
            <div class="col-sm-4 col-xs-12">
                <div class="row q-mb-sm">
                    <div>
                        <q-btn
                            id="start-production-button"
                            v-if="status == 'stopped'"
                            @click="startProductRun()"
                            :color="startButtonColor"
                            :disable="startButtonDisabled"
                            :label="$q.screen.lt.lg ? '' : $t('Start')"
                            icon="fas fa-play"
                            class="q-mr-sm large-button"
                        >
                            <q-tooltip v-if="startButtonDisabled">
                                {{ $t('Select a product to run') }}
                            </q-tooltip>
                        </q-btn>
                        <q-btn
                            id="changeover-button"
                            v-if="
                                status == 'running' || status == 'in_downtime'
                            "
                            @click="showChangeoverDialog = true"
                            :disable="$userCannot('create', 'phases')"
                            icon="fas fa-sync-alt"
                            :label="$q.screen.lt.lg ? '' : $t('Switch')"
                            text-color="dark-yellow"
                            outline
                            class="q-mr-sm bg-white large-button"
                        >
                            <q-tooltip>{{
                                $t('Start a new switch phase')
                            }}</q-tooltip>
                        </q-btn>
                        <div class="col-sm col-xs">
                            <q-btn
                                id="start-downtime-button"
                                v-if="status == 'changeover'"
                                @click="showDowntimeDialog = true"
                                icon="fas fa-exclamation-circle"
                                :label="
                                    $q.screen.lt.lg
                                        ? 'Downtime'
                                        : $t('Start downtime')
                                "
                                :disable="
                                    $userCannot('create', 'phases') ||
                                    timeDiff < DOWNTIME_TRIGGER_LIMIT
                                "
                                :color="
                                    ASSET_STATUS_TO_STYLE['in_downtime'][
                                        'color'
                                    ]
                                "
                                class="q-mr-sm large-button changeover-button"
                            ></q-btn>
                            <q-tooltip v-if="timeDiff < DOWNTIME_TRIGGER_LIMIT">
                                {{
                                    $t(
                                        'The ongoing switch phase is ending in less than 5 seconds. A downtime cannot be triggered manually.'
                                    )
                                }}
                            </q-tooltip>
                        </div>
                    </div>
                    <div class="col-sm col-xs">
                        <table-select
                            id="product-select"
                            v-model="currentProduct"
                            @confirmSelection="
                                currentProduct = productSelectProps.selection
                            "
                            :class="
                                status === 'running'
                                    ? 'product-select__disabled'
                                    : ''
                            "
                            :label="
                                status === 'stopped'
                                    ? $t('Select a product to run')
                                    : $t('Product')
                            "
                            :baseURL="productSelectUrl"
                            :dialogTitle="productSelectProps.dialogTitle"
                            :searchLabel="productSelectProps.searchLabel"
                            :dense="true"
                            :disable="status !== 'stopped'"
                            :clearable="false"
                            class="product-select full-width"
                            row-key="id"
                        >
                            <template v-slot:item="data">
                                <product-card
                                    @select="
                                        productSelectProps.selection = {
                                            label: data.props.row.name,
                                            value: data.props.row
                                        }
                                    "
                                    :name="data.props.row.name"
                                    :code="data.props.row.code"
                                    :description="data.props.row.description"
                                    :unit-measure="data.props.row.unit_measure"
                                    :unit-packing="data.props.row.unit_packing"
                                    :expected-run-rate="
                                        data.props.row.expected_run_rate
                                    "
                                    :isSelected="
                                        productSelectProps.selection &&
                                        productSelectProps.selection.value
                                            .product === data.props.row.product
                                    "
                                    :isSelectable="true"
                                ></product-card>
                            </template>
                        </table-select>
                        <q-tooltip
                            v-if="status !== 'stopped' && product"
                            class="bg-black"
                            style="font-size: 24px"
                            >{{ currentProduct.label }}</q-tooltip
                        >
                    </div>
                    <div>
                        <q-btn
                            id="shutdown-button"
                            v-if="status !== 'stopped'"
                            @click="showStopDialog = true"
                            :disable="$userCannot('create', 'phases')"
                            :label="$q.screen.gt.sm ? $t('Shutdown') : ''"
                            icon="fas fa-stop"
                            color="negative"
                            class="q-ml-sm bg-white large-button"
                            outline
                        >
                            <q-tooltip>{{ $t('Start a shutdown') }}</q-tooltip>
                        </q-btn>
                    </div>
                </div>
            </div>

            <div class="col-sm-4 col-xs-12">
                <div
                    class="row q-gutter-xs"
                    :class="$q.screen.gt.xs ? 'justify-end' : 'justify-center'"
                >
                    <q-btn
                        id="asset-timeline-btn"
                        @click="
                            $router.push({
                                name: 'asset_timeline',
                                params: {
                                    assetId: assetId
                                }
                            })
                        "
                        icon="fas fa-history"
                        color="accent"
                        class="bg-white"
                        size="md"
                        no-caps
                        outline
                    >
                        <q-tooltip>{{ $t('Asset Timeline') }}</q-tooltip>
                    </q-btn>
                    <q-btn
                        id="asset-settings-btn"
                        @click="
                            $router.push({
                                name: 'asset_settings',
                                params: {
                                    assetId: assetId
                                }
                            })
                        "
                        class="q-ml-sm bg-white"
                        icon="fas fa-cogs"
                        color="accent"
                        size="md"
                        outline
                    >
                        <q-tooltip>{{ $t('Asset settings') }}</q-tooltip>
                    </q-btn>
                    <q-btn
                        v-if="$userCan('edit', 'assets')"
                        @click="handleEditDashboard"
                        class="q-ml-sm bg-white"
                        icon="fas fa-edit"
                        color="accent"
                        size="md"
                        outline
                    >
                        <q-tooltip>{{ $t('Edit dashboard') }}</q-tooltip>
                    </q-btn>
                </div>
            </div>
        </div>

        <!-- Edit dashboard banner -->
        <div v-if="isEdit">
            <div class="row bg-secondary-white q-pb-sm items-center">
                <q-select
                    :model-value="selectedDashboardRole"
                    @update:model-value="
                        role =>
                            (this.selectedDashboardRole =
                                this.getRoleView(role))
                    "
                    :label="$t('Select view')"
                    :options="dashboardRoleOptions"
                    class="bg-white col-sm-2 col-xs-12 q-my-xs"
                    color="accent"
                    emit-value
                    map-options
                    outlined
                    dense
                />
                <div class="col-sm col-xs-12 q-px-sm ellipsis">
                    {{
                        $t('Drag and drop widgets to re-order your dashboard.')
                    }}
                </div>
                <div
                    align="right"
                    :class="
                        $q.screen.gt.xs ? 'col-md-6' : 'row col-xs-12 q-pa-xs'
                    "
                >
                    <q-btn
                        @click="showWidgetDialog = true"
                        :label="$t('Add Widget')"
                        :class="$q.screen.gt.xs ? '' : 'col-12 q-my-sm'"
                        color="accent"
                        outline
                    />
                    <q-btn
                        @click="saveWidgetChanges"
                        :label="$t('Save')"
                        :class="$q.screen.gt.xs ? 'q-mx-sm' : 'col-12 q-my-sm'"
                        color="accent"
                        outline
                    />
                    <q-btn
                        @click="discardWidgetChanges"
                        :label="$t('Discard')"
                        :class="$q.screen.gt.xs ? '' : 'col-12 q-my-sm'"
                        color="negative"
                        outline
                    />
                </div>
            </div>
        </div>

        <!-- Select widgets -->
        <select-widget-dialog
            v-if="showWidgetDialog"
            :selectedWidget="selectedWidget"
            :facilityId="parseInt(facilityId)"
            :assetId="parseInt(assetId)"
            :productId="parseInt(productId)"
            @addWidget="handleAddWidget($event)"
            @addWidgetWhileEditing="handleAddWidgetWhileEditing($event)"
            @close="closeSelectWidgetDialog"
            :unitMeasure="unitLabel"
        />

        <!-- No widgets prompt -->
        <div
            v-if="
                dashboard[roleView] &&
                dashboard[roleView].length === 0 &&
                !isEdit
            "
        >
            <span>
                {{ `${$t('This asset has no widgets')}.` }}
                <q-btn
                    @click="isEdit = true"
                    :disable="$userCannot('edit', 'assets')"
                    :label="$t('Edit dashboard')"
                    class="inline-button"
                    color="primary"
                    flat
                    no-caps
                    dense
                ></q-btn>
                {{ `${$t('to add some widgets')}.` }}
            </span>
        </div>

        <div v-if="performanceGraph">
            <div
                v-if="showChartRefreshButton && !performanceGraph.isLoading()"
                class="text-center q-ma-xl"
            >
                <q-btn
                    @click="
                        () => {
                            performanceGraph.loadData();
                        }
                    "
                    class="q-pa-xs"
                    icon="fas fa-sync-alt"
                    color="primary"
                    size="md"
                    outline
                    :label="$t('Refresh Graph')"
                >
                </q-btn>
            </div>

            <performance-graph
                :performance="performanceGraphData"
                :events="performanceGraphEvents"
                :isLoading="performanceGraph.isLoading()"
            ></performance-graph>
        </div>

        <!-- Cards -->
        <draggable
            v-model="dashboard[roleView]"
            class="row"
            @start="drag = true"
            @end="drag = false"
            :disabled="!isEdit"
            item-key="id"
        >
            <template v-slot:item="{ element, index }">
                <div class="col-lg-3 col-md-4 col-sm-6 col-xs-12" :key="index">
                    <asset-widget
                        @remove="handleRemoveWidget(element.type)"
                        @edit="handleEditWidget(element.type, index)"
                        :class="isEdit ? 'asset-card__draggable' : ''"
                        :type="element.type"
                        :data="assetData"
                        :showValue="
                            status === 'running' || status === 'in_downtime'
                        "
                        :is-edit="isEdit"
                        class="q-ma-md"
                        :speed-widget-color-levels="speedWidgetColorLevels"
                        :expected-run-rate="expectedRunRate"
                        :packagingFactors="getPackageFactor"
                        :sensorConfiguration="getSensorConfig"
                        :productUnitPackaging="productUnitPackaging"
                        :badgeType="getBadgeType(element.type)"
                    />
                </div>
            </template>
        </draggable>
        <!-- Cause select dialog -->
        <changeover-dialog
            v-if="showChangeoverDialog"
            @close="showChangeoverDialog = false"
            :assetId="assetId"
            :facilityId="facilityId"
            :currentProduct="currentProduct?.value"
        />

        <!-- Confirm stop dialog -->
        <q-dialog
            v-model="showStopDialog"
            @hide="
                {
                    stoppedCauseSelectProps.selection = null;
                    stoppedCauseSelectProps.description = '';
                }
            "
        >
            <q-card style="max-width: 375px">
                <q-bar class="bg-primary text-white">
                    <div>{{ $t('Shutdown') }}</div>

                    <q-space />

                    <q-btn
                        @click="showStopDialog = false"
                        dense
                        flat
                        icon="close"
                    >
                        <q-tooltip>{{ $t('Close') }}</q-tooltip>
                    </q-btn>
                </q-bar>

                <q-card-section class="row items-center">
                    <div>
                        {{
                            $t(
                                'Are you sure? ioTORQ will keep recording data for this asset but it will not be included in OEE metrics.'
                            )
                        }}
                    </div>
                </q-card-section>

                <q-card-section class="row items-center">
                    <div class="full-width">
                        <span class="q-mr-xs">
                            {{ $t('Select a cause for this shutdown') }}:</span
                        >
                        <table-select
                            id="shutdown-cause-select"
                            v-model="stoppedCauseSelectProps.selection"
                            :baseURL="stoppedCauseSelectProps.url"
                            :queryParams="stoppedCauseSelectProps.queryParams"
                            :label="stoppedCauseSelectProps.label"
                            :dialogTitle="stoppedCauseSelectProps.dialogTitle"
                            :searchLabel="stoppedCauseSelectProps.searchLabel"
                            :rules="[val => !!val || $t('Select a cause')]"
                            :noDataLabel="stoppedCauseSelectProps.noDataLabel"
                            row-key="id"
                        >
                            <template v-slot:item="data">
                                <cause-card
                                    @select="
                                        selectCause(
                                            data.props.row,
                                            stoppedCauseSelectProps
                                        )
                                    "
                                    :name="data.props.row.name"
                                    :lossType="data.props.row.type_of_loss"
                                    :categoryName="data.props.row.category_name"
                                    :planned="data.props.row.planned"
                                    :description="data.props.row.description"
                                    :targetDowntime="
                                        data.props.row.target_down_time
                                    "
                                    :isSelected="
                                        stoppedCauseSelectProps.selection &&
                                        stoppedCauseSelectProps.selection
                                            .value === data.props.row.id
                                    "
                                    :isSelectable="true"
                                    :equipmentFailure="
                                        data.props.row.equipment_failure
                                    "
                                />
                            </template>
                        </table-select>
                        <q-input
                            type="textarea"
                            v-model="stoppedCauseSelectProps.description"
                            @clear="stoppedCauseSelectProps.description = ''"
                            :label="$t('Enter a description')"
                            class="q-mb-sm q-pb-md shutdown-cause-description"
                            outlined
                            lazy-rules
                            clearable
                            color="accent"
                        />
                    </div>
                </q-card-section>

                <q-card-actions align="right">
                    <q-btn
                        id="cancel-shutdown-button"
                        @click="showStopDialog = false"
                        :label="$t('Cancel')"
                        color="primary"
                        outline
                    />
                    <q-btn
                        id="confirm-shutdown-button"
                        @click="
                            () => {
                                stopProductRun();
                            }
                        "
                        :label="$t('Confirm')"
                        color="primary"
                    />
                </q-card-actions>
            </q-card>
        </q-dialog>
        <!-- Confirm downtime dialog -->
        <q-dialog v-model="showDowntimeDialog">
            <q-card class="q-pa-sm" style="max-width: 375px">
                <q-card-section>
                    <div class="text-h6">
                        {{ $t('Trigger downtime') }}
                    </div>
                </q-card-section>

                <q-card-section class="row items-center">
                    <div>
                        {{ $t('Are you sure you want to start a downtime?') }}
                    </div>
                </q-card-section>

                <q-card-actions align="right">
                    <q-btn
                        @click="showDowntimeDialog = false"
                        :label="$t('Cancel')"
                        color="primary"
                        outline
                    />
                    <q-btn
                        id="confirm-downtime-button"
                        @click="
                            () => {
                                startManualDowntime();
                            }
                        "
                        :label="$t('Confirm')"
                        color="primary"
                    />
                </q-card-actions>
            </q-card>
        </q-dialog>

        <!-- Select cause if manual downtime is trigger-->
        <q-dialog v-model="showNewDowntimeDialog">
            <q-card class="text-white" style="width: 70%">
                <q-bar class="bg-primary" style="height: 35px">
                    <div>
                        {{ $t('Record a new downtime') }}
                    </div>
                </q-bar>
                <q-card-section class="q-px-lg q-py-none text-black">
                    <div class="q-pt-md">
                        {{ $t('Select a cause for the downtime') }}
                    </div>
                </q-card-section>
                <table-select
                    id="downtime-cause-select"
                    class="q-px-lg q-py-none"
                    v-model="cause"
                    @confirmSelection="cause = causeSelectProps.selection"
                    :baseURL="causeSelectProps.url"
                    :queryParams="causeSelectProps.queryParams"
                    :label="causeSelectProps.label"
                    :dialogTitle="causeSelectProps.dialogTitle"
                    :searchLabel="causeSelectProps.searchLabel"
                    :rules="[val => !!val || $t('Select a cause')]"
                    row-key="id"
                >
                    <template v-slot:item="data">
                        <cause-card
                            @select="
                                selectCause(data.props.row, causeSelectProps)
                            "
                            :name="data.props.row.name"
                            :lossType="data.props.row.type_of_loss"
                            :categoryName="data.props.row.category_name"
                            :planned="data.props.row.planned"
                            :description="data.props.row.description"
                            :targetDowntime="data.props.row.target_down_time"
                            :isSelected="
                                causeSelectProps.selection &&
                                causeSelectProps.selection.value ===
                                    data.props.row.id
                            "
                            :isSelectable="true"
                            :equipmentFailure="data.props.row.equipment_failure"
                        />
                    </template>
                </table-select>
                <q-card-actions align="right" class="q-pb-md">
                    <q-btn
                        @click="showNewDowntimeDialog = false"
                        :label="$t('Cancel')"
                        color="primary"
                        outline
                    />
                    <q-btn
                        id="confirm-downtime-cause-button"
                        @click="
                            () => {
                                enterManualDowntimeCause(cause);
                            }
                        "
                        :label="$t('Confirm')"
                        color="primary"
                        class="q-mr-md"
                    />
                </q-card-actions>
            </q-card>
        </q-dialog>

        <!-- Select cause for a new changeover phase -->
        <changeover-dialog
            v-if="showNewChangeoverDialog"
            @close="showNewChangeoverDialog = false"
            :assetId="assetId"
            :facilityId="facilityId"
            :currentProduct="currentProduct?.value"
            :cause="cause"
        />

        <!-- Split current downtime -->
        <split-downtime-dialog
            v-if="showSplitDowntimeDialog"
            @close="showSplitDowntimeDialog = false"
            :assetId="assetId"
        />

        <!-- DEBUG: Show/hide logs button -->
        <div class="row full-width">
            <div class="col">
                <q-btn
                    class="float-right asset-settings-btn q-my-lg q-mx-md ellipsis"
                    color="grey"
                    outline
                    v-show="isSuperuser"
                    :label="isLogsShow ? $t('Hide logs') : $t('Show logs')"
                    @click="isLogsShow = !isLogsShow"
                />
            </div>
        </div>

        <!-- DEBUG: Running statistics -->
        <div v-show="isLogsShow">
            <q-table
                :rows="assetEventsLog.contents"
                :columns="debugAssetTableColumns"
                v-model:pagination="debugTablePagination"
                title="Asset Events Log"
                row-key="timestamp"
                separator="vertical"
                class="q-mx-md q-mb-xl no-shadow border-grey"
                hide-bottom
                dense
                :no-data-label="$t('No data available')"
            />
            <div class="q-mx-md">
                <span>
                    <b>Events processed:</b>
                    {{ debugStats['asset']['nbEvents'] }}
                </span>
                <br />
                <span>
                    <b>Minimum delay:</b>
                    {{ debugStats['asset']['minDelay'] }}
                </span>
                <br />
                <span>
                    <b>Maximum delay:</b>
                    {{ debugStats['asset']['maxDelay'] }}
                </span>
                <br />
                <span>
                    <b>Average delay:</b>
                    {{ debugStats['asset']['avgDelay'] }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
// app imports
import AssetWidget from '@components/AssetWidget.vue';
import Banner from '@components/Banner.vue';
import CauseCard from '@components/CauseCard.vue';
import MissingCauseLabel from '@components/MissingCauseLabel.vue';
import PerformanceGraph from '@components/PerformanceGraph.vue';
import ProductCard from '@components/ProductCard.vue';
import SplitDowntimeDialog from '@components/SplitDowntimeDialog.vue';
import TableSelect from '@components/TableSelect.vue';
import UnlockChangeoverBanner from '@components/UnlockChangeoverBanner.vue';
import BreadcrumbsManager from '@mixins/BreadcrumbsManager.vue';
import { convertDurationToDHMS } from '@utils/time.js';
import Draggable from 'vuedraggable';
import ChangeoverDialog from './ChangeoverDialog.vue';
import Debug from './Debug.vue';
import EditDashboard from './EditDashboard.vue';
import SelectWidgetDialog from './SelectWidgetDialog.vue';

// 3rd party
import ConnectionManager from '@src/connectionManager.js';
import { mapGetters } from 'vuex';

// Status refresh using sensors
import {
    ASSET_STATUS_TO_STYLE,
    SENSOR_TYPES_TO_LABELS
} from '@models/assets.js';
import { DEVICE_STATUS, DEVICE_STATUS_STYLE } from '@models/devices.js';
import { WIDGET_TYPE_TO_PROPS } from '@models/widgets.js';
import getEventValue from '../../utils/eventValue.js';
import Graph from './PerformanceGraph.js';
import { mapActions, mapState } from 'pinia';
import { useSettingsStore } from '@stores/settings.js';
import { useSharedStore } from '@stores/shared.js';
import { useProductsStore } from '@stores/products.js';
import { useBannersStore } from '@stores/banners.js';
import { useAuthStore } from '@stores/auth';
import { useCausesStore } from '@stores/causes.js';

import { colors } from 'quasar';
const { getPaletteColor } = colors;

const SENSOR_MAX_DELAY = 5;
const DOWNTIME_TRIGGER_LIMIT = 5000;
const EXPECTED_DOWNTIME_DURATION_COLOR_CHANGE_LIMIT = 0.25;
const GRAPH_TIME_RANGE_HOURS = 12; // hours
const GRAPH_GAP_TIME_RANGE_MINUTES = 5 * 60 * 1000;
const GRAPH_REFRESH_TIME_RANGE_MINUTES = 1 * 60 * 1000;

export default {
    name: 'AssetDashboard',
    components: {
        AssetWidget,
        Banner,
        CauseCard,
        ChangeoverDialog,
        Draggable,
        PerformanceGraph,
        ProductCard,
        SelectWidgetDialog,
        TableSelect,
        SplitDowntimeDialog,
        MissingCauseLabel,
        UnlockChangeoverBanner
    },
    mixins: [BreadcrumbsManager, Debug, EditDashboard],
    props: {
        assetId: {
            type: [String, Number],
            required: true
        },
        facilityId: {
            type: [String, Number],
            required: true
        }
    },
    async mounted() {
        // check if asset doesn't exist
        // redirect to 404 page if not
        // this is done to prevent the page from breaking when
        // directly loading the page in the browser URL with an assetId that does not exist
        if (!this.asset) {
            this.$router.push({ name: '404' });
        }

        // Load current quality event, if any
        await this.loadQualityEvent();

        // Get asset current phase
        await this.getAssetCurrentPhase();
    },
    created() {
        // wait until signalr is connected before registering handler
        this.poll = setInterval(() => {
            console.debug('Checking if SignalR is connected...');
            if (ConnectionManager.isConnected()) {
                // register handler
                ConnectionManager.addHandler({
                    name: 'newStreamEvent',
                    handler: this.handleNewStreamEvent
                });
                clearInterval(this.poll);
            }
        }, 100);
        // Initially set role option as the current user role
        this.selectedDashboardRole = this.roleView;
    },
    async beforeUnmount() {
        // Reset timer countdown
        this.resetTimer();
        // destroy poller, so on logout we stop trying to subscribe to the signalr group
        clearInterval(this.poll);
        // Status refresh for sensors
        clearInterval(this.sensorsPoll);

        if (ConnectionManager.isInitialized()) {
            // remove handler
            await ConnectionManager.removeHandler('newStreamEvent');
            // Check if user is logged in before sending a request.
            // This check is important in the event that the user logs out from the asset dashboard page
            if (await this.isAuthenticatedAction()) {
                // unsubscribe from this asset
                await ConnectionManager.unsubscribe(this.assetId);
            }
        }
        this.dismissSensorDisabledBannerAction();
    },
    watch: {
        /**
         * Handle changing assets when on the same route (i.e. by using the drawer).
         */
        assetId: {
            immediate: true,
            async handler(currId, prevId) {
                // Status refresh using sensors
                clearInterval(this.sensorsPoll);

                this.sensorsPoll = setInterval(this.checkSensorStatus, 5000);
                // reset asset
                this.resetAsset();
                // initialize widgets
                this.initWidgets();
                // set breadcrumbs
                this.setBreadcrumbs([
                    {
                        label: this.$t('Facilities'),
                        route: {
                            name: 'facility_list'
                        }
                    },
                    {
                        label: this.facility.name,
                        route: {
                            name: 'facility_dashboard',
                            params: {
                                facilityId: this.facility.id
                            }
                        }
                    },
                    {
                        label: this.asset ? this.asset.name : null
                    }
                ]);

                // load the performance graph data
                await this.loadPerformanceDowntimeGraph();

                // reload the list of sensors from the API if needed
                await this.initializeSensors();

                await this.checkSensorStatus();

                // check if user is switching assets
                if (prevId) {
                    await ConnectionManager.unsubscribe(prevId);
                }

                // Load current quality event, if any
                await this.loadQualityEvent();
            }
        },
        /**
         * Updates the id of the cause as set from the asset phase signalr handler,
         * and start the countdown on changeover phase.
         * If the current changeover phase has the same cause as the previous one,
         * this watcher will not be executed.
         */
        causeId: {
            immediate: true,
            async handler(id) {
                // Reset current cause state
                this.cause = null;
                if (id) {
                    try {
                        this.loadingCause = true;
                        await this.loadCauseAction(id);
                        const cause = this.getCause(id);
                        if (cause) {
                            this.cause = cause;
                            if (this.phase.status === 'changeover') {
                                // begin countdown timer
                                this.beginCountdown();
                            }
                        } else {
                            this.$notifyError(
                                this.$t(
                                    'Failed to load cause details. The cause could have been deleted.'
                                )
                            );
                        }
                    } catch (error) {
                        this.$notifyError(
                            this.$t(
                                'Failed to load cause details. The cause could have been deleted.'
                            )
                        );
                        console.error(error);
                    } finally {
                        this.loadingCause = false;
                    }
                }
            }
        },
        defectId: {
            immediate: true,
            async handler(id) {
                if (id === null) {
                    this.defect = null;
                    return;
                }
                try {
                    const response = await this.$axios.get(
                        `/defects/${id}/?facility=${this.facilityId}`
                    );
                    const defectDetails = response.data;
                    if (defectDetails) {
                        this.defect = {
                            id: defectDetails.id,
                            name: defectDetails.name,
                            code: defectDetails.code,
                            description: defectDetails.description
                        };
                    }
                } catch (error) {
                    console.error('Error fetching defect', error);
                }
            }
        },
        /**
         * The id of the product as set from the asset phase signalr handler.
         * If the current product changes, load the product object from the API.
         */
        productId: {
            immediate: true,
            async handler(id) {
                // check if phase has a product id
                if (id && this.assetId) {
                    try {
                        // fetch the data
                        const productLink = await this.loadProductLinkAction({
                            facilityId: this.facilityId,
                            assetId: this.assetId,
                            productId: id
                        });
                        if (productLink) {
                            // update product link
                            this.productLink = this.getProductLink({
                                assetId: this.assetId,
                                productId: id
                            });
                        } else {
                            this.productLink = null;
                            this.$t(
                                this.$notifyError(
                                    'Failed to load product details.'
                                )
                            );
                        }
                    } catch (error) {
                        this.$t(
                            this.$notifyError('Failed to load product details.')
                        );
                        console.error(error);
                    }
                }

                // if product link is loaded, set product selector
                if (this.product) {
                    this.currentProduct = {
                        label: this.product.name,
                        value: this.product
                    };
                }
            }
        },
        /**
         * Ensure that countdown timer is stopped when status changes.
         */
        status(val) {
            if (val !== 'changeover') {
                // make sure to reset the countdown if we are not
                // anymore in a changeover
                this.resetTimer();
            }
            // when one of the clients begins a 'stopped' phase, signalr triggers the handler that
            // updates the status to be 'stopped', we want to clear the product selectors for every
            // client.
            if (val === 'stopped') {
                this.clearProductSelect();
            }
        },
        /**
         * Ensure that the phase has an id.
         * The signalR handle does not provide the phase id, so we need to fetch it from the API.
         */
        phase: {
            immediate: true,
            async handler(oldPhase, newPhase) {
                if (
                    !newPhase?.id ||
                    !oldPhase?.id ||
                    (newPhase.id && oldPhase?.id && oldPhase.id !== newPhase.id)
                ) {
                    await this.getAssetCurrentPhase();
                }
            }
        }
    },
    data() {
        return {
            performanceGraph: null,
            // models
            ASSET_STATUS_TO_STYLE,
            // Status refresh using sensors
            SENSOR_TYPES_TO_LABELS,
            DEVICE_STATUS,
            DEVICE_STATUS_STYLE,
            WIDGET_TYPE_TO_PROPS,
            DOWNTIME_TRIGGER_LIMIT,
            EXPECTED_DOWNTIME_DURATION_COLOR_CHANGE_LIMIT,
            GRAPH_TIME_RANGE_HOURS,
            GRAPH_GAP_TIME_RANGE_MINUTES,
            GRAPH_REFRESH_TIME_RANGE_MINUTES,

            // product selector
            productSelectProps: {
                dialogTitle: this.$t('Select Product'),
                searchLabel: this.$t('Search product'),
                selection: null
            },
            // selectors
            causeSelectProps: {
                url: `/assets/${this.assetId}/causes/`,
                queryParams: { ordering: 'name' },
                label: this.$t('Cause'),
                dialogTitle: this.$t('Select a cause'),
                searchLabel: this.$t('Search cause'),
                selection: null
            },
            stoppedCauseSelectProps: {
                url: `/assets/${this.assetId}/causes/`,
                queryParams: {
                    ordering: 'name',
                    type_of_loss: 'shutdown'
                },
                label: this.$t('Cause (Optional)'),
                dialogTitle: this.$t('Select a cause'),
                searchLabel: this.$t('Search cause'),
                selection: null,
                noDataLabel: this.$t(
                    'No planned shutdown cause is available for this line.'
                ),
                description: ''
            },

            currentProduct: {
                label: '',
                value: null
            },

            // active cause/product/defect
            cause: null,
            defect: null,
            productLink: null,
            manualDowntimeCausePayload: {},
            manualDowntimeCausePhaseId: null,

            // dialogs
            showChangeoverDialog: false,
            showStopDialog: false,
            showDowntimeDialog: false,
            showNewDowntimeDialog: false,
            showNewChangeoverDialog: false,
            showSplitDowntimeDialog: false,

            // streaming data
            productionAssetData: {},
            rejectAssetData: {},
            latestProductionTimestamp: null,
            latestRejectTimestamp: null,
            latestAssetTimestamp: null,

            // dashboard widgets
            dashboard: { operator: [], manager: [] },
            // when dashboard is in edit mode
            selectedDashboardRole: null,

            // countdown timer
            setIntervalFuncId: null,
            timeDiff: 0,

            // signalr subscribe poll
            poll: null,
            // Status refresh using sensors
            initializingSensors: false,
            deviceStatuses: {},
            sensorsPoll: null,

            showChartRefreshButton: false,
            loadingCause: false
        };
    },
    computed: {
        ...mapGetters({
            getAsset: 'facilities/getAsset',
            getFacility: 'facilities/getFacility',
            getAssetStatus: 'facilities/getAssetStatus',
            getQualityEvent: 'facilities/getQualityEvent',
            getAssetProduct: 'facilities/getAssetProduct',
            getAssetPhase: 'facilities/getAssetPhase'
        }),
        ...mapState(useSettingsStore, ['isTrackingQualityEvents']),
        ...mapState(useSharedStore, ['isLoading']),
        ...mapState(useProductsStore, ['getProductLink']),
        ...mapState(useAuthStore, {
            getUserLocale: 'getUserLocale',
            userRole: 'getUserRole',
            isSuperuser: 'isSuperuser'
        }),
        ...mapState(useCausesStore, ['getCause']),
        performanceGraphData() {
            if (!this.performanceGraph) {
                return [];
            }
            const perfData = this.performanceGraph.getPerformanceData();
            return perfData;
        },
        performanceGraphEvents() {
            return this.performanceGraph
                ? this.performanceGraph.getDowntimeEventsData()
                : [];
        },
        /**
         * Get speed widget color levels
         * @return {Array}
         */
        speedWidgetColorLevels() {
            return this.asset.speed_widget_color_levels;
        },
        /**
         * Get product object.
         * @return {Object}
         */
        product() {
            if (this.productLink) {
                return {
                    id: this.productLink.product,
                    name: this.productLink.name,
                    facility: this.facilityId,
                    code: this.productLink.code,
                    description: this.productLink.description,
                    unit_packing: this.productLink.unit_packing,
                    unit_measure: this.productLink.unit_measure
                };
            } else {
                return null;
            }
        },

        /**
         * Get asset object.
         * @return {Object}
         */
        asset() {
            return this.getAsset(this.assetId);
        },
        /**
         * Current facility.
         * @return {Object}
         */
        facility() {
            return this.getFacility(this.facilityId);
        },
        /**
         * Get asset status.
         * @return {String}
         */
        status() {
            const status = this.getAssetStatus(this.assetId);
            if (status) {
                return status;
            } else {
                return 'unknown';
            }
        },
        /**
         * List of sensors linked to the asset
         * @return {Array} - List of sensors.
         */
        sensors() {
            return this.$store.getters['facilities/getSensors'](this.assetId);
        },
        /**
         * Get device status.
         * @return {String} - The device status.
         */
        connectionStatus() {
            // if we have no information from sensors or devices, consider we are online
            if (this.sensors === null || this.deviceStatuses === null) {
                return 'online';
            }

            // if the asset has no sensor, consider it's disconnected
            if (this.sensors.length === 0) {
                return 'disconnected';
            }

            for (var deviceId in this.deviceStatuses) {
                if (this.deviceStatuses[deviceId] === DEVICE_STATUS.offline) {
                    // if at least one device is disconnected, then consider the asset as disconnected
                    return 'offline';
                }
            }
            // otherwise the asset is connected
            return 'online';
        },
        /**
         * Get asset phase.
         * @return {Object}
         */
        phase() {
            return this.getAssetPhase(this.assetId);
        },
        /**
         * Get cause id associated with phase, if there is one.
         * @return {(Number|null)} - The cause ID.
         */
        causeId() {
            if (this.phase) {
                return this.phase.cause;
            } else {
                return null;
            }
        },
        /**
         * Get the defect ID associated with the current quality event, if there is one.
         * @return {(Number|null)} - The defect ID.
         */
        defectId() {
            const qualityEvent = this.getQualityEvent(this.assetId);
            if (qualityEvent && qualityEvent.eventDefectID) {
                return qualityEvent.eventDefectID;
            } else {
                return null;
            }
        },
        /**
         * Get product id associated with phase, if there is one.
         * @return {(Number|null)}
         */
        productId() {
            if (this.phase) {
                return this.phase.currentProduct;
            } else {
                return null;
            }
        },
        /**
         * Get the phase id.
         * @return {Number}
         */
        phaseId() {
            return this.phase.id;
        },
        /**
         * Get expected run rate
         * @return {(Number|null)}
         */
        expectedRunRate() {
            if (this.productLink) {
                return Number(
                    this.productLink['expected_run_rates'][0]['value']
                );
            } else {
                return null;
            }
        },
        /**
         * URL to fetch products from for this asset.
         * @return {String}
         */
        productSelectUrl() {
            return `/assets/${this.assetId}/products/`;
        },
        /**
         * Start button color.
         * @return {String}
         */
        startButtonColor() {
            if (this.status === 'running' || this.currentProduct?.value) {
                return 'positive';
            }
            return 'grey';
        },
        /**
         * Determine if start button should be disabled.
         * Disable the start button if the asset is stopped and there is no product selected or user is not allowed.
         * @return {Boolean}
         */
        startButtonDisabled() {
            if (this.status === 'stopped') {
                let noProductSelected = true;

                // Check if current product is exists and has a value property
                if (this.currentProduct && 'value' in this.currentProduct) {
                    // Check if current product value is not null
                    if (this.currentProduct.value !== null) {
                        noProductSelected = false;
                    }
                }
                return (
                    noProductSelected || this.$userCannot('create', 'phases')
                );
            }
            return true;
        },
        /**
         * Get the role view depending on the user role or the selected dashboard role (edit mode)
         * If the user is in edit mode, then we retrieve the selected dashboard role
         * If the user is not in edit mode, then we retrieve the user's actual role
         * @return {String}
         */
        roleView() {
            if (this.isEdit) {
                return this.selectedDashboardRole;
            } else {
                return this.getRoleView(this.userRole);
            }
        },
        /**
         * View options for dashboard (i.e. view dashboard as 'operator' or 'manager' in edit mode)
         * @return {Object[]} - List of options available.
         */
        dashboardRoleOptions() {
            // Operators can only see the operator dashboard
            const options = [
                {
                    label: this.$t('Operator'),
                    value: 'operator'
                }
            ];
            // Managers and Admins can see the manager dashboard
            if (this.userRole === 'manager' || this.userRole === 'admin') {
                options.push({
                    label: this.$t('Manager'),
                    value: 'manager'
                });
            }
            return options;
        },
        /**
         * Get the unit label for the running product.
         * @return {String}
         */
        unitLabel() {
            if (this.product) {
                return this.product.unit_measure;
            } else {
                return '';
            }
        },
        /**
         * Get asset data.
         * @return {Object}
         */
        assetData() {
            return {
                ...this.productionAssetData,
                ...this.rejectAssetData
            };
        },
        /**
         * Update counter style based on remaining time
         * @return {String}
         */
        getCountdownStyle() {
            return this.timeDiff <=
                // if the remaining time is less than 25% of the expected down time
                EXPECTED_DOWNTIME_DURATION_COLOR_CHANGE_LIMIT *
                    this.cause.target_down_time *
                    60000
                ? `background-color: ${getPaletteColor('dark-red')};`
                : `background-color: ${getPaletteColor('negative')};`;
        },
        /**
         * Update widget visibility based on the asset status
         * @return {Boolean}
         */
        showWidgets() {
            return this.status !== 'changeover' || this.isEdit;
        },
        /**
         * Determine if the phase cause would be disabled.
         * @return {boolean}
         */
        showPhaseCause() {
            if (
                (this.status === 'in_downtime' || this.status === 'stopped') &&
                this.cause !== null &&
                this.cause.name
            ) {
                return true;
            }
            return false;
        },
        /**
         * Get the packaging factor for the product.
         * @return {Object|null}
         */
        getPackageFactor() {
            const packageFactor = this.productLink?.packaging_factors;
            if (!packageFactor || typeof packageFactor !== 'object') {
                return {
                    production: 1.0,
                    net: 1.0,
                    reject: 1.0
                };
            }
            return this.productLink.packaging_factors;
        },
        /**
         * Get sensor configuration for the asset.
         * @return {String} - Sensor configuration e.g. 'gross-reject'
         */
        getSensorConfig() {
            return this.asset['sensor_configuration'];
        },
        /**
         * Get product unit packaging.
         * @return {Number} - Product unit packaging.
         */
        productUnitPackaging() {
            return this.productLink?.unit_packing;
        },
        /**
         * Verify if the current asset has an ongoing quality event.
         * @return {Boolean} - True if the asset has an ongoing quality event.
         */
        hasOngoingQualityEvent() {
            const event = this.getQualityEvent(this.assetId);
            return event && event.end === null;
        },
        /**
         * Determines whether to show the unlock changeover dialog.
         * @returns {boolean} - `true` if the unlock changeover dialog should be shown, `false` otherwise.
         */
        showUnlockChangeoverDialog() {
            const currentPhase = this.getAssetPhase(this.assetId);
            return (
                currentPhase?.status === 'changeover' &&
                currentPhase?.lockedTransitionToRunning
            );
        },
        /**
         * Determines the appropriate factor for calculations based on
         * the sensor configuration and package factors.
         * @returns {number} The factor to use for calculations.
         */
        getFactor() {
            const factors = this.getPackageFactor;

            switch (this.getSensorConfig) {
                case 'gross-reject':
                    return factors.production;
                case 'net-reject':
                    return factors.net;
                case 'gross-net':
                    return factors.production !== 1 && factors.net === 1
                        ? factors.production
                        : factors.net;
                default:
                    return 1; // default if configuration is unknown or not set
            }
        }
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        ...mapActions(useProductsStore, ['loadProductLinkAction']),
        ...mapActions(useBannersStore, [
            'displaySensorDisabledBannerAction',
            'dismissSensorDisabledBannerAction'
        ]),
        ...mapActions(useAuthStore, ['isAuthenticatedAction']),
        ...mapActions(useCausesStore, ['loadCauseAction']),
        /**
         * Get the role view to use for dashboard.
         * e.g.
         * If the user is a operator, then the role view will be 'operator'
         * If the user is a viewer, then the role view will be 'manager'
         * If the user is a manager, then the role view will be 'manager'
         * If the user is an admin, then the role view will be 'manager'
         * @param {String} role - User role.
         * @return {String} - Role view to use for dashboard.
         */
        getRoleView(role) {
            if (role === 'manager' || role === 'viewer' || role === 'admin') {
                return 'manager';
            } else {
                // default to operator
                return 'operator';
            }
        },
        /**
         * Send a request to update cause in a phase
         * @param {Number} cause - Cause id.
         */
        async enterManualDowntimeCause(cause) {
            const errorMessage = this.$t(
                'Failed to update phase at this time. Please try again later.'
            );

            let causeValue = null;

            // check if cause is not null and has a value property
            if (cause && cause['value']) {
                causeValue = cause['value'];
            }

            if (!causeValue) {
                this.$notifyError(errorMessage);
                return;
            }
            // set the cause to update in the phase in the payload
            this.manualDowntimeCausePayload['cause'] = causeValue;
            await this.$axios
                .patch(
                    `assetphases/${this.manualDowntimeCausePhaseId}/`,
                    this.manualDowntimeCausePayload
                )
                .then(response => {
                    // notify user
                    this.$notifySuccess(this.$t('Updated phase.'));
                })
                .catch(e => {
                    const error =
                        e?.response?.data && typeof e.response.data === 'object'
                            ? e.response.data
                            : {};
                    this.$notifyError(errorMessage, error);
                })
                .finally(() => {
                    // close modal

                    this.showNewDowntimeDialog = false;
                });
        },
        /**
         * Initialize widgets from the asset store.
         */
        initWidgets() {
            // if asset is null or user does not have access to asset, then return
            if (!this.asset) {
                return;
            }

            const dashboard = this.asset['dashboard'];
            // if dashboard is not null, use the provided values
            if (dashboard) {
                // check if dashboard keys are valid
                if ('operator' in dashboard && 'manager' in dashboard) {
                    // set dashboard (deep copy to prevent mutating value in store)
                    this.dashboard = JSON.parse(
                        JSON.stringify(this.asset['dashboard'])
                    );
                    this.removeUnavailableWidgets();
                }
            }
        },
        /**
         * Widgets created on the backend but not yet recognized on
         * the frontend
         */
        removeUnavailableWidgets() {
            for (const roleview in this.dashboard) {
                this.dashboard[roleview] = this.dashboard[roleview].filter(
                    widget => {
                        return this.WIDGET_TYPE_TO_PROPS[widget['type']];
                    }
                );
            }
        },
        /**
         * Clear product selector.
         */
        clearProductSelect() {
            this.currentProduct = {
                label: '',
                value: null
            };
        },

        /**
         * Handle start product run.
         */
        async startProductRun() {
            const errorMessage = this.$t(
                'Failed to start product run. Please try again later.'
            );
            const missingProductError = this.$t('Select a product to run');
            const product = this.currentProduct.value?.product ?? null;

            if (!product) {
                this.$notifyError(missingProductError);
                return;
            }

            this.beginLoading(
                `${this.$t('Starting product run. Please wait')}...`
            );
            const payload = {
                phase: 'running',
                product: product
            };
            try {
                await this.$axios.post(
                    `assets/${this.assetId}/phases/start`,
                    payload
                );
            } catch (error) {
                this.$notifyError(errorMessage, error);
            } finally {
                this.stopLoading();
            }
        },
        /**
         * Handle start downtime.
         */
        async startManualDowntime() {
            this.showDowntimeDialog = false;
            /**
             * Case when the user has this dialog box open but the asset goes to running/downtime.
             * In that situation, we don't want the user to manually start a downtime. This additional
             * test would prevent this as the timeDiff would be reset to 0.
             * Case There is also the case when the user has this dialog box open but the asset goes
             * to running/downtime. In that situation, we don't want the user to manually start a downtime.
             * This additional test would prevent this as the timeDiff would be reset to 0
             */
            if (this.timeDiff > DOWNTIME_TRIGGER_LIMIT) {
                let product_id = this.productId;
                if (product_id) {
                    // start loading
                    this.beginLoading(`${this.$t('Starting downtime')}...`);
                    const startDowntimeErrorMessage = this.$t(
                        'Failed to start downtime. Please try again later.'
                    );
                    try {
                        let response = await this.$axios.post(
                            `assets/${this.assetId}/phases/start`,
                            {
                                phase: 'in_downtime',
                                product: this.productId
                            }
                        );

                        if (response.status !== 200) {
                            this.$notifyError(
                                this.$t(
                                    'Failed to start downtime. Please try again later.'
                                )
                            );
                        } else {
                            // if downtime is started successfully, then ask the user to enter the cause
                            // open the cause dialog for the user to enter the cause
                            this.showNewDowntimeDialog = true;
                            // get the phase id of the phase that was started
                            this.manualDowntimeCausePhaseId =
                                response.data['id'];
                            // build the payload and add asset id to it
                            this.manualDowntimeCausePayload['asset'] =
                                response.data['asset'];
                        }
                    } catch (error) {
                        this.$notifyError(startDowntimeErrorMessage, error);
                    } finally {
                        // stop loading
                        this.stopLoading();
                    }
                } else {
                    this.$notifyError(
                        this.$t('Please select a product to start a downtime.')
                    );
                }
            } else {
                this.$notifyError(
                    this.$t(
                        'The ongoing switch phase is ending in less than 5 seconds. A downtime cannot be triggered manually.'
                    )
                );
            }
        },
        /**
         * Handle stop product run.
         */
        async stopProductRun() {
            this.showStopDialog = false;
            this.beginLoading(`${this.$t('Shutting down product run')}...`);
            const errorMessage = this.$t(
                'Failed to shutdown product run. Please try again later.'
            );

            try {
                const payload = {
                    phase: 'stopped',
                    cause:
                        this.stoppedCauseSelectProps.selection?.value || null,
                    description: this.stoppedCauseSelectProps?.description || ''
                };
                // start a stopped phase
                await this.$axios.post(
                    `assets/${this.assetId}/phases/start`,
                    payload
                );

                this.clearProductSelect();
            } catch (error) {
                this.$notifyError(errorMessage, error);
            } finally {
                this.stopLoading();
            }
        },
        /**
         * Get the quantity (L, kg) of product per unit
         * For instance, is the product is currently 1.5 litre bottle of milk
         * THen this value is 1.5
         * @return {Number}
         */
        getQuantityPerUnit() {
            if (this.product) {
                return parseFloat(this.product.unit_packing);
            } else {
                // if we don't know the product, then we cannot convert
                return null;
            }
        },
        /**
         * Given the name of the widget, checks if the widget
         * should use "packages" or "units" as the unit of measurement
         * @param {String} widgetName
         * @return {Boolean}
         */
        usePackages(widgetName) {
            // Check if this is for a "package" count widget
            if (
                [
                    'currentPackageCount',
                    'currentRejectPackageCount',
                    'currentNetPackageCount',
                    'packageSpeedLast1Min',
                    'packageSpeedLast5Min',
                    'packageSpeedLast60Min',
                    'packageSpeedLast1MinPerHour',
                    'packageSpeedLast5MinPerHour',
                    'packageSpeedLast60MinPerHour'
                ].includes(widgetName)
            ) {
                // Use packages if the primary factor is greater than or equal to 1, otherwise use units
                return this.getFactor >= 1;
            }

            // Default to packages for non-package widgets
            return true;
        },
        /**
         * Truncates the decimals from a float number
         * @param {Number} number - the number to truncate
         * @return {Int} Truncated integer
         */
        truncateDecimals(number) {
            return parseInt(Math[number < 0 ? 'ceil' : 'floor'](number));
        },
        /**
         * Get asset data from stream event.
         * Point of entry from streaming data. Maps widget types to corresponding event data.
         * @param {Object} event - Event data.
         * @return {Object} Keys used in frontend.
         */
        getAssetDataFromEvent(event) {
            this.performanceGraph.pushLiveEventData(event);

            // get the quantity of item (litre/kg) per product unit
            const unitToQuantity = this.getQuantityPerUnit();

            // needed values:
            var totalCountCurrentDay = getEventValue({
                event: event,
                key: 'totalCountCurrentDay'
            });
            var totalCountCurrentDayReject = getEventValue({
                event: event,
                key: 'totalCountCurrentDayReject'
            });
            var currentProductCount = getEventValue({
                event: event,
                key: 'currentProductCount'
            });
            var currentProductReject = getEventValue({
                event: event,
                key: 'currentProductReject'
            });
            var currentShiftCount = getEventValue({
                event: event,
                key: 'currentShiftCount'
            });
            var currentShiftReject = getEventValue({
                event: event,
                key: 'currentShiftReject'
            });
            var totalCountYesterday = getEventValue({
                event: event,
                key: 'totalCountYesterday'
            });
            var totalCountYesterdayReject = getEventValue({
                event: event,
                key: 'totalCountYesterdayReject'
            });

            // calculated oee values:
            var qualityCurrentDay = this.calculateQuality(
                totalCountCurrentDay,
                totalCountCurrentDayReject
            );
            var qualityYesterday = this.calculateQuality(
                totalCountYesterday,
                totalCountYesterdayReject
            );
            var qualityCurrentProduct = this.calculateQuality(
                currentProductCount,
                currentProductReject
            );
            var qualityCurrentShift = this.calculateQuality(
                currentShiftCount,
                currentShiftReject
            );

            var productionQuantityLast60MinKg = getEventValue({
                event: event,
                key: 'productionQuantityLast60Min',
                inner_key: 'kg'
            });
            var productionQuantityLast60MinL = getEventValue({
                event: event,
                key: 'productionQuantityLast60Min',
                inner_key: 'litre'
            });
            var rejectQuantityLast60MinKg = getEventValue({
                event: event,
                key: 'rejectQuantityLast60Min',
                inner_key: 'kg'
            });
            var rejectQuantityLast60MinL = getEventValue({
                event: event,
                key: 'rejectQuantityLast60Min',
                inner_key: 'litre'
            });

            return {
                delay: this.eventDelay(event),
                timestamp: event.timestamp,
                receivedAt: event.receivedAt,
                sensorId: getEventValue({
                    event: event,
                    key: 'sensorId'
                }),
                currentCauseId: getEventValue({
                    event: event,
                    key: 'currentCause'
                }),
                currentProductId: getEventValue({
                    event: event,
                    key: 'currentProduct'
                }),
                // Product
                currentProductCount: {
                    value: this.truncateDecimals(currentProductCount),
                    unit: this.$t('products')
                },
                currentProductUnit: {
                    value: currentProductCount * unitToQuantity,
                    unit: this.unitLabel
                },
                productionQuantityLast60MinKg: {
                    value: productionQuantityLast60MinKg,
                    unit: 'kg'
                },
                productionQuantityLast60MinLitre: {
                    value: productionQuantityLast60MinL,
                    unit: 'litre'
                },
                rejectQuantityLast60MinKg: {
                    value: rejectQuantityLast60MinKg,
                    unit: 'kg'
                },
                rejectQuantityLast60MinLitre: {
                    value: rejectQuantityLast60MinL,
                    unit: 'litre'
                },
                currentNetProductCount: {
                    value: this.truncateDecimals(
                        currentProductCount - currentProductReject
                    ),
                    unit: this.$t('products')
                },
                currentNetProductUnit: {
                    value:
                        (currentProductCount - currentProductReject) *
                        unitToQuantity,
                    unit: this.unitLabel
                },
                // Shift
                currentShiftCount: {
                    value: this.truncateDecimals(currentShiftCount),
                    unit: this.$t('products')
                },
                currentShiftUnit: {
                    value: getEventValue({
                        event: event,
                        key: 'currentShiftCount',
                        inner_key: null,
                        scale: unitToQuantity
                    }),
                    unit: this.unitLabel
                },
                currentShiftCurrentProductCount: {
                    value: this.truncateDecimals(
                        getEventValue({
                            event: event,
                            key: 'currentShiftCurrentProductCount'
                        })
                    ),
                    unit: this.$t('products')
                },
                currentShiftCurrentProductUnit: {
                    value: this.truncateDecimals(
                        getEventValue({
                            event: event,
                            key: 'currentShiftCurrentProductCount',
                            inner_key: null,
                            scale: unitToQuantity
                        })
                    ),
                    unit: this.unitLabel
                },
                currentNetShiftCount: {
                    value: this.truncateDecimals(
                        currentShiftCount - currentShiftReject
                    ),
                    unit: this.$t('products')
                },
                currentShiftNetCurrentProductUnit: {
                    value: this.getCurrentShiftNetCurrentProductUnit(
                        event,
                        unitToQuantity
                    ),
                    unit: this.unitLabel
                },
                // Average speed in products/min
                speedCountLast1Min: {
                    value: getEventValue({
                        event: event,
                        key: 'avgSpeedLast1Min'
                    }),
                    unit: this.$t('products/min')
                },
                speedCountLast5Min: {
                    value: getEventValue({
                        event: event,
                        key: 'avgSpeedLast5Min'
                    }),
                    unit: this.$t('products/min')
                },
                speedCountLast60Min: {
                    value: getEventValue({
                        event: event,
                        key: 'avgSpeedLast60Min'
                    }),
                    unit: this.$t('products/min')
                },
                currentPackageCount: {
                    value: this.getPackageCount(event),
                    unit: this.usePackages('currentPackageCount')
                        ? this.$t('packages')
                        : this.$t('units')
                },
                currentNetPackageCount: {
                    value: this.getNetPackageCount(event),
                    unit: this.usePackages('currentNetPackageCount')
                        ? this.$t('packages')
                        : this.$t('units')
                },
                currentRejectPackageCount: {
                    value: this.getRejectPackageCount(event),
                    unit: this.usePackages('currentRejectPackageCount')
                        ? this.$t('packages')
                        : this.$t('units')
                },
                // Average speed in  units/hour
                speedCountLast1MinPerHour: {
                    value: getEventValue({
                        event: event,
                        key: 'avgSpeedLast1Min',
                        inner_key: null,
                        scale: 60
                    }),
                    unit: this.$t('products/hour')
                },
                speedCountLast5MinPerHour: {
                    value: getEventValue({
                        event: event,
                        key: 'avgSpeedLast5Min',
                        inner_key: null,
                        scale: 60
                    }),
                    unit: this.$t('products/hour')
                },
                speedCountLast60MinPerHour: {
                    value: getEventValue({
                        event: event,
                        key: 'avgSpeedLast60Min',
                        inner_key: null,
                        scale: 60
                    }),
                    unit: this.$t('products/hour')
                },
                // Average speed in packages/min
                packageSpeedLast1Min: {
                    value: getEventValue({
                        event: event,
                        key: 'avgPackageSpeedLast1Min'
                    }),
                    unit: this.usePackages('packageSpeedLast1Min')
                        ? this.$t('packages/min')
                        : this.$t('units/min')
                },
                packageSpeedLast5Min: {
                    value: getEventValue({
                        event: event,
                        key: 'avgPackageSpeedLast5Min'
                    }),
                    unit: this.usePackages('packageSpeedLast5Min')
                        ? this.$t('packages/min')
                        : this.$t('units/min')
                },
                packageSpeedLast60Min: {
                    value: getEventValue({
                        event: event,
                        key: 'avgPackageSpeedLast60Min'
                    }),
                    unit: this.usePackages('packageSpeedLast60Min')
                        ? this.$t('packages/min')
                        : this.$t('units/min')
                },
                // Average speed in packages/hour
                packageSpeedLast1MinPerHour: {
                    value: getEventValue({
                        event: event,
                        key: 'avgPackageSpeedLast1Min',
                        inner_key: null,
                        scale: 60
                    }),
                    unit: this.usePackages('packageSpeedLast1MinPerHour')
                        ? this.$t('packages/hour')
                        : this.$t('units/hour')
                },
                packageSpeedLast5MinPerHour: {
                    value: getEventValue({
                        event: event,
                        key: 'avgPackageSpeedLast5Min',
                        inner_key: null,
                        scale: 60
                    }),
                    unit: this.usePackages('packageSpeedLast5MinPerHour')
                        ? this.$t('packages/hour')
                        : this.$t('units/hour')
                },
                packageSpeedLast60MinPerHour: {
                    value: getEventValue({
                        event: event,
                        key: 'avgPackageSpeedLast60Min',
                        inner_key: null,
                        scale: 60
                    }),
                    unit: this.usePackages('packageSpeedLast60MinPerHour')
                        ? this.$t('packages/hour')
                        : this.$t('units/hour')
                },
                // Average speed in quantity/min (ex: litre/min)
                speedQuantityLast1Min: {
                    value: getEventValue({
                        event: event,
                        key: 'avgSpeedLast1Min',
                        inner_key: null,
                        scale: unitToQuantity
                    }),
                    unit: this.$t('{unit}/min', { unit: this.unitLabel })
                },
                speedQuantityLast5Min: {
                    value: getEventValue({
                        event: event,
                        key: 'avgSpeedLast5Min',
                        inner_key: null,
                        scale: unitToQuantity
                    }),
                    unit: this.$t('{unit}/min', { unit: this.unitLabel })
                },
                speedQuantityLast60Min: {
                    value: getEventValue({
                        event: event,
                        key: 'avgSpeedLast60Min',
                        inner_key: null,
                        scale: unitToQuantity
                    }),
                    unit: this.$t('{unit}/min', { unit: this.unitLabel })
                },
                // Average speed in quantity/hour (ex: litre/hour)
                speedQuantityLast1MinPerHour: {
                    value: getEventValue({
                        event: event,
                        key: 'avgSpeedLast1Min',
                        inner_key: null,
                        scale: unitToQuantity * 60
                    }),
                    unit: this.$t('{unit}/hour', { unit: this.unitLabel })
                },
                speedQuantityLast5MinPerHour: {
                    value: getEventValue({
                        event: event,
                        key: 'avgSpeedLast5Min',
                        inner_key: null,
                        scale: unitToQuantity * 60
                    }),
                    unit: this.$t('{unit}/hour', { unit: this.unitLabel })
                },
                speedQuantityLast60MinPerHour: {
                    value: getEventValue({
                        event: event,
                        key: 'avgSpeedLast60Min',
                        inner_key: null,
                        scale: unitToQuantity * 60
                    }),
                    unit: this.$t('{unit}/hour', { unit: this.unitLabel })
                },
                // Downtime - counts
                downtimeProductCount: {
                    value: getEventValue({
                        event: event,
                        key: 'downtimeProductCount'
                    }),
                    unit: ''
                },
                downtimeShiftCount: {
                    value: getEventValue({
                        event: event,
                        key: 'downtimeShiftCount'
                    }),
                    unit: ''
                },
                downtimeShiftUnjustified: {
                    value: getEventValue({
                        event: event,
                        key: 'downtimeShiftUnjustified'
                    }),
                    unit: ''
                },
                // Downtime - durations
                shiftDowntimeInSec: {
                    value: getEventValue({
                        event: event,
                        key: 'shiftDowntimeInSec'
                    }),
                    unit: ''
                },
                productDowntimeInSec: {
                    value: getEventValue({
                        event: event,
                        key: 'productDowntimeInSec'
                    }),
                    unit: ''
                },
                currDowntimeInSec: {
                    value: getEventValue({
                        event: event,
                        key: 'currDowntimeInSec'
                    }),
                    unit: ''
                },
                currentPhaseTotalTime: {
                    value: this.getCurrentPhaseTotalTime(),
                    unit: ''
                },
                // Rejects
                currentProductReject: {
                    value: this.truncateDecimals(currentProductReject),
                    unit: this.$t('products')
                },
                currentShiftReject: {
                    value: this.truncateDecimals(currentShiftReject),
                    unit: this.$t('products')
                },
                currentShiftCurrentProductReject: {
                    value: this.truncateDecimals(
                        getEventValue({
                            event: event,
                            key: 'currentShiftCurrentProductReject'
                        })
                    ),
                    unit: this.$t('products')
                },
                // Totals
                totalCountCurrentDay: {
                    value: this.truncateDecimals(totalCountCurrentDay),
                    unit: this.$t('products')
                },
                totalCountCurrentDayReject: {
                    value: this.truncateDecimals(totalCountCurrentDayReject),
                    unit: this.$t('products')
                },
                totalCountYesterday: {
                    value: this.truncateDecimals(totalCountYesterday),
                    unit: this.$t('products')
                },
                totalCountYesterdayReject: {
                    value: this.truncateDecimals(totalCountYesterdayReject),
                    unit: this.$t('products')
                },
                totalNetCountCurrentDay: {
                    value: this.truncateDecimals(
                        totalCountCurrentDay - totalCountCurrentDayReject
                    ),
                    unit: this.$t('products')
                },
                // Quality
                qualityCurrentDay: {
                    value: qualityCurrentDay,
                    unit: '%'
                },
                qualityCurrentProduct: {
                    value: qualityCurrentProduct,
                    unit: '%'
                },
                qualityCurrentShift: {
                    value: qualityCurrentShift,
                    unit: '%'
                },
                qualityYesterday: {
                    value: qualityYesterday,
                    unit: '%'
                },
                quality1Min: {
                    value: getEventValue({
                        event: event,
                        key: 'quality1Min',
                        inner_key: null,
                        scale: 100
                    }),
                    unit: '%'
                },
                quality5Min: {
                    value: getEventValue({
                        event: event,
                        key: 'quality5Min',
                        inner_key: null,
                        scale: 100
                    }),
                    unit: '%'
                },
                quality60Min: {
                    value: getEventValue({
                        event: event,
                        key: 'quality60Min',
                        inner_key: null,
                        scale: 100
                    }),
                    unit: '%'
                },
                uptimeCurrentDay: {
                    value: getEventValue({
                        event: event,
                        key: 'uptimeCurrentDay',
                        inner_key: null,
                        scale: 100
                    }),
                    unit: '%'
                },
                uptimeYesterday: {
                    value: getEventValue({
                        event: event,
                        key: 'uptimeYesterday',
                        inner_key: null,
                        scale: 100
                    }),
                    unit: '%'
                },
                uptimeCurrentShift: {
                    value: getEventValue({
                        event: event,
                        key: 'uptimeCurrentShift',
                        inner_key: null,
                        scale: 100
                    }),
                    unit: '%'
                },
                uptimeCurrentProduct: {
                    value: getEventValue({
                        event: event,
                        key: 'uptimeCurrentProduct',
                        inner_key: null,
                        scale: 100
                    }),
                    unit: '%'
                },
                uptime1Min: {
                    value: getEventValue({
                        event: event,
                        key: 'uptime1Min',
                        inner_key: null,
                        scale: 100
                    }),
                    unit: '%'
                },
                uptime5Min: {
                    value: getEventValue({
                        event: event,
                        key: 'uptime5Min',
                        inner_key: null,
                        scale: 100
                    }),
                    unit: '%'
                },
                uptime60Min: {
                    value: getEventValue({
                        event: event,
                        key: 'uptime60Min',
                        inner_key: null,
                        scale: 100
                    }),
                    unit: '%'
                },
                performanceCurrentDay: {
                    value: getEventValue({
                        event: event,
                        key: 'performanceCurrentDay',
                        inner_key: null,
                        scale: 100
                    }),
                    unit: '%'
                },
                performanceYesterday: {
                    value: getEventValue({
                        event: event,
                        key: 'performanceYesterday',
                        inner_key: null,
                        scale: 100
                    }),
                    unit: '%'
                },
                performanceCurrentShift: {
                    value: getEventValue({
                        event: event,
                        key: 'performanceCurrentShift',
                        inner_key: null,
                        scale: 100
                    }),
                    unit: '%'
                },
                performanceCurrentProduct: {
                    value: getEventValue({
                        event: event,
                        key: 'performanceCurrentProduct',
                        inner_key: null,
                        scale: 100
                    }),
                    unit: '%'
                },
                performance1Min: {
                    value: getEventValue({
                        event: event,
                        key: 'performance1Min',
                        inner_key: null,
                        scale: 100
                    }),
                    unit: '%'
                },
                performance5Min: {
                    value: getEventValue({
                        event: event,
                        key: 'performance5Min',
                        inner_key: null,
                        scale: 100
                    }),
                    unit: '%'
                },
                performance60Min: {
                    value: getEventValue({
                        event: event,
                        key: 'performance60Min',
                        inner_key: null,
                        scale: 100
                    }),
                    unit: '%'
                },
                oeeCurrentDay: {
                    value: this.calculateOEE(
                        qualityCurrentDay / 100,
                        getEventValue({
                            event: event,
                            key: 'performanceCurrentDay'
                        }),
                        getEventValue({
                            event: event,
                            key: 'uptimeCurrentDay'
                        })
                    ),
                    unit: '%'
                },
                oeeYesterday: {
                    value: this.calculateOEE(
                        qualityYesterday / 100,
                        getEventValue({
                            event: event,
                            key: 'performanceYesterday'
                        }),
                        getEventValue({
                            event: event,
                            key: 'uptimeYesterday'
                        })
                    ),
                    unit: '%'
                },
                oeeCurrentShift: {
                    value: this.calculateOEE(
                        qualityCurrentShift / 100,
                        getEventValue({
                            event: event,
                            key: 'performanceCurrentShift'
                        }),
                        getEventValue({
                            event: event,
                            key: 'uptimeCurrentShift'
                        })
                    ),
                    unit: '%'
                },
                oeeCurrentProduct: {
                    value: this.calculateOEE(
                        qualityCurrentProduct / 100,
                        getEventValue({
                            event: event,
                            key: 'performanceCurrentProduct'
                        }),
                        getEventValue({
                            event: event,
                            key: 'uptimeCurrentProduct'
                        })
                    ),
                    unit: '%'
                },
                oee1Min: {
                    value: this.calculateOEE(
                        getEventValue({
                            event: event,
                            key: 'quality1Min'
                        }),
                        getEventValue({
                            event: event,
                            key: 'performance1Min'
                        }),
                        getEventValue({
                            event: event,
                            key: 'uptime1Min'
                        })
                    ),
                    unit: '%'
                },
                oee5Min: {
                    value: this.calculateOEE(
                        getEventValue({
                            event: event,
                            key: 'quality5Min'
                        }),
                        getEventValue({
                            event: event,
                            key: 'performance5Min'
                        }),
                        getEventValue({
                            event: event,
                            key: 'uptime5Min'
                        })
                    ),
                    unit: '%'
                },
                oee60Min: {
                    value: this.calculateOEE(
                        getEventValue({
                            event: event,
                            key: 'quality60Min'
                        }),
                        getEventValue({
                            event: event,
                            key: 'performance60Min'
                        }),
                        getEventValue({
                            event: event,
                            key: 'uptime60Min'
                        })
                    ),
                    unit: '%'
                }
            };
        },
        calculateQuality(grossProd, rejectProd) {
            /**
             * Calculate the quality given the gross production and reject production
             * @param {int} grossProd - Gross production
             * @param {int} rejectProd - Reject production
             * @return {float|str}
             */
            if (
                [grossProd, rejectProd].some(
                    num => isNaN(num) || num === undefined || num === null
                )
            ) {
                return '-';
            }

            if (grossProd === 0) {
                return 100.0;
            }

            return 100 * ((grossProd - rejectProd) / grossProd);
        },
        calculateOEE(quality, performance, uptime) {
            /**
             * Calculate the oee given the quality performance and uptime
             * @param quality - quality
             * @param performance - performance
             * @param uptime - uptime
             * @return {float|str}
             */
            if (
                [quality, performance, uptime].some(
                    num => isNaN(num) || num === undefined || num === null
                )
            ) {
                return '-';
            }

            return quality * performance * uptime * 100;
        },
        /**
         * Check if an event has the latest timestamp.
         * @param {Date} latestTimestamp - Current latest timestamp.
         * @param {Date} eventTimestamp - Event timestamp.
         * @return {Bool}
         */
        isLatestTimestamp(latestTimestamp, eventTimestamp) {
            return latestTimestamp === null || latestTimestamp < eventTimestamp;
        },

        /**
         * Get widget current phase total time,
         * @return {Date}
         */
        getCurrentPhaseTotalTime() {
            return Math.round(
                new Date().getTime() / 1000 -
                    new Date(this.phase.start).getTime() / 1000
            );
        },
        /**
         * Callback to execute when there is a new stream event.
         * @param {Object} event - Stream event data.
         */
        handleNewStreamEvent(event) {
            console.debug('New event data received');
            // record when event is received
            event['receivedAt'] = new Date().toISOString();

            // check if event is for this asset
            if (this.asset.uuid !== event['asset_uuid']) {
                // do nothing, don't process this message
                return;
            }

            const assetData = this.getAssetDataFromEvent(event);

            // get field values based on sensor type
            if ('sensorType' in event) {
                if (event['sensorType'] === 'production') {
                    if (
                        this.isLatestTimestamp(
                            this.latestProductionTimestamp,
                            event['timestamp']
                        )
                    ) {
                        // isolate reject keys from production asset data
                        const {
                            currentProductReject,
                            currentShiftReject,
                            totalCountCurrentDayReject,
                            totalCountYesterdayReject,
                            ...productionAssetData
                        } = assetData;
                        this.productionAssetData = productionAssetData;
                        // update latest timestamp
                        this.latestProductionTimestamp = event['timestamp'];
                        this.updateStats(assetData, event['sensorType']);
                    }
                } else {
                    if (
                        this.isLatestTimestamp(
                            this.latestRejectTimestamp,
                            event['timestamp']
                        )
                    ) {
                        this.rejectAssetData = {
                            currentProductReject:
                                assetData['currentProductReject'],
                            currentShiftReject: assetData['currentShiftReject'],
                            totalCountCurrentDayReject:
                                assetData['totalCountCurrentDayReject'],
                            totalCountYesterdayReject:
                                assetData['totalCountYesterdayReject']
                        };
                        // update latest timestamp
                        this.latestRejectTimestamp = event['timestamp'];
                        this.updateStats(assetData, 'asset');
                    }
                }
            } else {
                if (
                    this.isLatestTimestamp(
                        this.latestAssetTimestamp,
                        event['timestamp']
                    )
                ) {
                    const {
                        currentProductReject,
                        currentShiftReject,
                        totalCountCurrentDayReject,
                        totalCountYesterdayReject,
                        ...productionAssetData
                    } = assetData;
                    this.productionAssetData = productionAssetData;
                    // update latest timestamp
                    this.rejectAssetData = {
                        currentProductReject: assetData['currentProductReject'],
                        currentShiftReject: assetData['currentShiftReject'],
                        totalCountCurrentDayReject:
                            assetData['totalCountCurrentDayReject'],
                        totalCountYesterdayReject:
                            assetData['totalCountYesterdayReject']
                    };
                    // update latest timestamp
                    this.latestAssetTimestamp = event['timestamp'];

                    // collect events for logging
                    this.assetEventsLog.push(assetData);
                    this.updateStats(assetData, 'asset');
                }
            }
        },

        /**
         * Reset asset states.
         */
        resetAsset() {
            this.cause = null;
            this.productLink = null;
            this.rejectAssetData = {};
            this.productionAssetData = {};
            this.latestProductionTimestamp = null;
            this.latestRejectTimestamp = null;
            this.latestAssetTimestamp = null;
            this.showChangeoverDialog = false;
            // clear the list of sensors and statuses
            this.deviceStatuses = null;
            // reset debug logs
            this.resetStats();
            this.assetEventsLog.clear();

            // reset sensor disabled notification
            this.dismissSensorDisabledBannerAction();
        },
        /**
         * Runs the countdown timer for a changeover phase.
         */
        beginCountdown() {
            if (!this.phase?.start || !this.cause?.target_down_time) {
                return this.$notifyError(
                    this.$t('Failed to begin changeover countdown.')
                );
            }
            // Reset last switch phase countdown timer
            if (this.setIntervalFuncId) {
                this.resetTimer();
            }
            // update timeDiff every one second
            this.setIntervalFuncId = setInterval(() => {
                const phaseStartTime = new Date(this.phase.start);
                // add the cause target down time minutes to determine when this changeover is expected to end
                const stopTimeTarget = new Date(
                    phaseStartTime.getTime() +
                        this.cause?.target_down_time * 60000
                );
                // get the time difference between when the changeover is expected to end and the time now
                this.timeDiff = stopTimeTarget - new Date().getTime();
                if (this.timeDiff <= 0) {
                    // stop timer
                    clearInterval(this.setIntervalFuncId);
                }
            }, 1000);
        },

        /**
         * Reset countdown timer.
         */
        resetTimer() {
            clearInterval(this.setIntervalFuncId);
            this.timeDiff = 0;
        },
        /**
         * Get the status of a device given the device id
         * @param {object} sensor - sensor
         * @return {function} - Callable that returns boolean if sensor is delayed.
         */
        isSensorLate(sensor) {
            // Delay in data from production sensors
            let latestTimestamp = null;
            if (this.latestAssetTimestamp !== null) {
                // asset message schema
                latestTimestamp = this.latestAssetTimestamp;
            } else {
                // legacy sensor message schema
                if (sensor.sensor_type === 'production') {
                    latestTimestamp = this.latestProductionTimestamp;
                } else if (sensor.sensor_type === 'reject') {
                    latestTimestamp = this.latestRejectTimestamp;
                }
            }
            return this.isLate(latestTimestamp);
        },
        /**
         * compare delay from latest sensor
         * @param {Date} latestTimestamp - latest timestamp for most recent data received
         * @return {boolean} - returns true if data was received late past the MAX_DELAY.
         */
        isLate(latestTimestamp) {
            // Get current time
            const currentTime = new Date().getTime() / 1000;
            const Delay = currentTime - latestTimestamp;
            if (Delay > SENSOR_MAX_DELAY) {
                return true;
            }
            return false;
        },
        /**
         * Update Device status
         */
        async updateDeviceStatus(deviceId) {
            await this.$store
                .dispatch('facilities/getDeviceStatus', deviceId)
                .then(response => {
                    this.deviceStatuses[deviceId] = response.deviceStatus;
                })
                .catch(error => {
                    console.error(
                        'Failed to fetch device status data due to: ',
                        error
                    );
                    // initialize deviceStatuses object if null
                    if (!this.deviceStatuses) {
                        this.deviceStatuses = {};
                    }
                    // if we don't know the status of the device, consider it is online
                    this.deviceStatuses[deviceId] = DEVICE_STATUS.online;
                });
        },
        /**
         * Get the sensors for the current asset from store if present
         * If not present retrieve from server
         * Set device statuses as unknown
         */
        async initializeSensors() {
            // get sensors selected for this asset
            if (this.sensors === null) {
                this.initializingSensors = true;
                const errorMessage = this.$t(
                    'Failed load the list of sensors for this asset'
                );
                await this.$store
                    .dispatch('facilities/loadSensors', this.assetId)
                    .then(response => {
                        this.initializeDeviceStatuses();
                    })
                    .catch(error => {
                        this.$notifyError(errorMessage);
                        console.error(
                            'Failed to fetch list of sensors due to: ',
                            error
                        );
                    })
                    .finally(() => {
                        // stop loading
                        this.stopLoading();
                        this.initializingSensors = false;
                    });
            } else {
                this.initializeDeviceStatuses();
            }
        },
        /**
         * Initialize Device Status
         */
        initializeDeviceStatuses() {
            this.deviceStatuses = {};
            if (this.sensors) {
                // if one of the sensors connected to this device
                // is disabled, show a notification banner on top
                if (this.sensors.some(sensor => sensor.is_enabled === false)) {
                    this.displaySensorDisabledBannerAction();
                }
                for (const sensor of this.sensors) {
                    // by default, all devices are considered to be online
                    this.deviceStatuses[sensor.device] = DEVICE_STATUS.online;
                }
            }
        },
        async checkSensorStatus() {
            if (this.sensors) {
                for (var sensor of this.sensors) {
                    let isLate = this.isSensorLate(sensor);
                    const deviceId = sensor.device;
                    if (isLate) {
                        // get device status
                        await this.updateDeviceStatus(deviceId);
                    } else {
                        // initialize deviceStatuses object if null
                        if (!this.deviceStatuses) {
                            this.deviceStatuses = {};
                        }
                        // set device status to connected
                        this.deviceStatuses[deviceId] = DEVICE_STATUS.online;
                    }
                }
            }
        },
        /**
         * Loads the performance data and asset's
         * downtime events from the api.
         */
        async loadPerformanceDowntimeGraph() {
            this.performanceGraph = new Graph({
                facilityId: this.facilityId,
                assetId: this.assetId
            });

            await this.performanceGraph.loadData();
        },
        /**
         * Get the delay between the time when event started and time when
         * the event was received in the system.
         * @param {object} event from the SignalR.
         */
        eventDelay(event) {
            if (event.timestamp && event.receivedAt) {
                const eventDate = new Date(0).setUTCSeconds(event.timestamp);
                const receivedDate = new Date(event.receivedAt);
                return Math.abs(receivedDate - eventDate) / 1000;
            }
        },
        /**
         * Format milliseconds into hh:mm:ss.
         * @param {Number} ms - Milliseconds.
         * @return {String}
         */
        countdownFormat(ms) {
            return convertDurationToDHMS(ms / 1000, true);
        },
        /**
         * Calculate the package count for the asset based on the sensor configuration
         * @param {Object} event - Event data
         * @return {Number} - Package count
         */
        getPackageCount(event) {
            const gross = event['currentProductCount'];

            if (isNaN(gross) || !this.getSensorConfig) {
                return '-';
            }

            if (this.getFactor === 0) {
                return this.truncateDecimals(gross);
            }

            return this.truncateDecimals(gross / this.getFactor);
        },

        /**
         * Calculate the net package count for the asset
         * @param {Object} event - Event data
         * @return {Number} - Net package count
         */
        getNetPackageCount(event) {
            const gross = event['currentProductCount'];
            const reject = event['currentProductReject'];

            if (isNaN(gross) || isNaN(reject) || !this.getSensorConfig) {
                return '-';
            }

            const net = gross - reject;

            if (this.getFactor === 0) {
                return this.truncateDecimals(net);
            }

            return this.truncateDecimals(net / this.getFactor);
        },

        /**
         * Calculate the reject package count for the asset
         * @param {Object} event - Event data
         * @return {Number} - Reject package count
         */
        getRejectPackageCount(event) {
            const reject = event['currentProductReject'];

            if (isNaN(reject) || !this.getSensorConfig) {
                return '-';
            }

            if (this.getFactor === 0) {
                return this.truncateDecimals(reject);
            }

            return this.truncateDecimals(reject / this.getFactor);
        },
        /**
         * Get current shift net current product count
         * @param {Object} event - Event data
         * @param {Number} unitToQuantity - Quantity per unit
         * @return {Number} - Net current product count
         */
        getCurrentShiftNetCurrentProductUnit(event, unitToQuantity) {
            const currentShiftCount = getEventValue({
                event: event,
                key: 'currentShiftCurrentProductCount',
                inner_key: null,
                scale: unitToQuantity
            });
            const currentShiftReject = getEventValue({
                event: event,
                key: 'currentShiftCurrentProductReject',
                inner_key: null,
                scale: unitToQuantity
            });

            // check if the values are numbers
            if (
                [currentShiftCount, currentShiftReject].some(
                    num =>
                        isNaN(num) ||
                        num === null ||
                        num === undefined ||
                        typeof num !== 'number'
                )
            ) {
                return '-';
            }

            return currentShiftCount - currentShiftReject;
        },

        /**
         * Update the asset current phase by dispatching an action to load the current phase.
         * @returns {Promise<void>} A promise that resolves when the action is dispatched.
         */
        async getAssetCurrentPhase() {
            await this.$store.dispatch('facilities/loadCurrentPhases', {
                assetId: this.assetId
            });
        },
        /**
         * Select/deselect causes in table-select component
         * @param {Object} row - Event from table row.
         * @param {Object} props - Table selection props
         */
        selectCause(row, props) {
            if (props.selection && props.selection.value === row.id) {
                props.selection = null;
            } else {
                props.selection = {
                    label: row.name,
                    value: row.id
                };
            }
        },
        /**
         * Handle closing of the select widget dialog
         */
        closeSelectWidgetDialog() {
            this.showWidgetDialog = false;
            this.selectedWidget = null;
        },
        /**
         * Load current quality event info for the asset
         */
        async loadQualityEvent() {
            // Do not load quality events if the feature is disabled
            if (!this.isTrackingQualityEvents) {
                return;
            }
            try {
                await this.$store.dispatch(
                    'facilities/loadQualityEvent',
                    this.assetId
                );
            } catch (error) {
                this.$notifyError(
                    this.$t('Failed to fetch quality events data.'),
                    error
                );
            }
        }
    }
};
</script>

<style lang="scss" scoped>
.asset-settings-btn {
    z-index: 1000;
}

.changeover-text {
    font-size: 14px;
    line-height: 14px;
}

.changeover-button {
    font-size: 12px;
}

.inline-button :deep(.q-btn__wrapper) {
    padding: 0px;
}

.product-select :deep(.q-field__inner) {
    border-radius: 3px;
    font-size: 18px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: 900;
}

.status {
    text-align: center;
}

.status__label {
    line-height: 40px;
    font-size: 28px;
    font-weight: 700;
    color: $secondary;
}

.status__icon {
    // aligns status icon with status label
    margin-bottom: 5px;
}

// draggable classes
.asset-card__draggable {
    cursor: grab;
}

.drag {
    opacity: 0;
}

.ghost {
    background-color: $warning;
}

:deep(.q-field__control) {
    background-color: white;
}

// countdown timer badge
.countdown-timer {
    height: 40px;
    width: 160px;
    font-size: 25px;
    color: white;
    border-radius: 4px;
}

// product selector style when q-input is in disabled mode
.product-select :deep(.q-field--labeled.q-field--dense .q-field__native) {
    font-weight: 700;
    color: black;
}

.large-button {
    padding-top: 8px;
    padding-bottom: 8px;
}
</style>
